import React from "react";
import Button from "components/Button/Regular";
import { ReactComponent as PlusIcon } from "assets/icons/plus.svg";
import { useNavigate } from "react-router-dom";
import Page from "components/Page/Page";
import Table from "components/Table/Table";
import { PaginationBar } from "components/Pagination";
import PageSizeSelect from "components/Table/PageSizeSelect";
import { useTableView } from "components/Table/useTableView";
import { useAuth0 } from "@auth0/auth0-react";
import { useActiveOrg } from "context/ActiveOrgContext";
import { deleteOrgAnimal, getPatientsList, PatientRow } from "services/patients.service";
import patientFallbackImage from "assets/images/patient-fallback.svg";
import vgToast from "utils/toast";

const PatientsList = () => {
  const navigate = useNavigate();
  const { getAccessTokenSilently, isAuthenticated } = useAuth0();
  const { orgId } = useActiveOrg();
  const [patients, setPatients] = React.useState<PatientRow[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);

  // Client-side paging and sorting: the list endpoints return whole
  // collections with no paging parameters, so there is nothing to page
  // server-side against.
  const view = useTableView(patients);

  const load = React.useCallback(async () => {
    if (!isAuthenticated || orgId <= 0) {
      setPatients([]);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const accessToken = await getAccessTokenSilently();
      const { data, error: apiError } = await getPatientsList(accessToken, orgId);
      if (apiError) {
        setError(apiError.message);
        setPatients([]);
        return;
      }
      setPatients(data ?? []);
    } catch (err) {
      setError((err as Error).message);
      setPatients([]);
    } finally {
      setLoading(false);
    }
  }, [getAccessTokenSilently, isAuthenticated, orgId]);

  React.useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async (id: string) => {
    if (!window.confirm("Delete this patient? This cannot be undone.")) return;
    if (orgId <= 0) return;
    try {
      const token = await getAccessTokenSilently();
      const { error: apiError } = await deleteOrgAnimal(token, orgId, Number(id));
      if (apiError) {
        vgToast.error(apiError.message);
        return;
      }
      await load();
    } catch (e) {
      vgToast.error((e as Error).message);
    }
  };

  return (
    <Page
      title="Patients"
      subTitle="Manage the animals registered with your clinic."
      actions={
        <Button onClick={() => navigate("/app/patients/new")}>
          <PlusIcon />
          <span>Add New Patient</span>
        </Button>
      }
    >
      <PageSizeSelect
        value={view.pageSize}
        onChange={view.setPageSize}
        className="mb-3"
      />

      <Table<{
        id: string;
        image: string;
        name: string;
        type: string;
      }>
        loading={loading}
        columns={{
          image: "Photo",
          id: "Patient Id",
          name: "Patient Name",
          type: "Species",
        }}
        rows={view.pageRows}
        sort={view.sort}
        onSortChange={view.toggleSort}
        emptyMessage="No patients yet. Add your first patient to get started."
        renderCell={(colId, row) => {
          if (colId !== "image") return undefined;
          return (
            <img
              src={row.image || patientFallbackImage}
              alt={row.name || "patient"}
              onError={(e) => {
                const img = e.currentTarget;
                if (img.src !== patientFallbackImage) img.src = patientFallbackImage;
              }}
              className="h-10 w-10 rounded-full object-cover border border-vg-blue/20 bg-vg-blue/[0.04]"
            />
          );
        }}
        handleViewItem={(id) => navigate(`/app/patients/${id}`)}
        handleRemoveItem={handleDelete}
      />
      {error && <div className="mt-3 text-sm text-red-500">{error}</div>}

      <div className="mt-4">
        <PaginationBar
          from={view.from}
          to={view.to}
          total={view.total}
          currentPage={view.page}
          totalPages={view.totalPages}
          onPrev={view.goPrev}
          onNext={view.goNext}
          onPageSelect={view.goToPage}
        />
      </div>
    </Page>
  );
};

export default PatientsList;
